import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { showNotification } from 'actions/notification';
import { useGetTools, usePostToolTest } from 'actions/chatbot';

import {
  Container,
  LeftPanel,
  RightPanel,
  EmptyState,
  ListItem,
  WriteBadge,
  DetailPanel,
  ToolName,
  ToolDescription,
  ParamSection,
  ParamLabel,
  ParamInput,
  ParamHint,
  RunButton,
  ConfirmRow,
  ResultBox,
  ResultMeta,
  RequiredMark,
} from './styled-comps';

function ToolsTab() {
  const dispatch = useDispatch();
  const [selectedTool, setSelectedTool] = useState(null);
  const [params, setParams] = useState({});
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  const {
    get: getTools,
    data: toolsData,
    isProcessing: loading,
    error: toolsError,
  } = useGetTools();

  const { post: postToolTest } = usePostToolTest({
    onComplete: (errorMessage, data) => {
      setRunning(false);
      if (errorMessage) {
        setResult({ error: errorMessage });
      } else {
        setResult(data);
      }
    },
  });

  const tools = Array.isArray(toolsData) ? toolsData : [];

  const fetchTools = useCallback(() => {
    getTools('list');
  }, [getTools]);

  useEffect(() => {
    fetchTools();
  }, [fetchTools]);

  useEffect(() => {
    if (toolsError) {
      dispatch(
        showNotification({
          text: 'Failed to fetch tools',
          duration: 5,
          showCloseIcon: true,
        }),
      );
    }
  }, [toolsError, dispatch]);

  const handleSelect = tool => {
    setSelectedTool(tool);
    setResult(null);
    setConfirmed(false);
    // Initialize params from schema
    const initial = {};
    const properties = (tool.input_schema || {}).properties || {};
    Object.keys(properties).forEach(key => {
      initial[key] = '';
    });
    setParams(initial);
  };

  const handleParamChange = (key, value) => {
    setParams(prev => ({ ...prev, [key]: value }));
  };

  const handleRun = () => {
    if (!selectedTool) return;
    setRunning(true);
    setResult(null);
    postToolTest({ parameters: params, confirmed }, selectedTool.name);
  };

  const renderParamForm = () => {
    if (!selectedTool) return null;
    const schema = selectedTool.input_schema || {};
    const properties = schema.properties || {};
    const required = schema.required || [];

    return Object.entries(properties).map(([key, prop]) => (
      <ParamSection key={key}>
        <ParamLabel>
          {key} {required.includes(key) && <RequiredMark>*</RequiredMark>}
        </ParamLabel>
        <ParamInput
          value={params[key] || ''}
          onChange={e => handleParamChange(key, e.target.value)}
          placeholder={prop.description || key}
        />
        {prop.description && <ParamHint>{prop.description}</ParamHint>}
      </ParamSection>
    ));
  };

  return (
    <Container>
      <LeftPanel>
        {loading && !Array.isArray(tools) ? (
          <EmptyState>Loading...</EmptyState>
        ) : loading && tools.length === 0 ? (
          <EmptyState>Loading...</EmptyState>
        ) : (
          (Array.isArray(tools) ? tools : []).map(tool => (
            <ListItem
              key={tool.name}
              $selected={selectedTool && selectedTool.name === tool.name}
              onClick={() => handleSelect(tool)}
            >
              <span>{tool.name}</span>
              {tool.is_write && <WriteBadge>WRITE</WriteBadge>}
            </ListItem>
          ))
        )}
        {!loading && Array.isArray(tools) && !tools.length && (
          <EmptyState>No tools registered</EmptyState>
        )}
      </LeftPanel>
      <RightPanel>
        {selectedTool ? (
          <DetailPanel>
            <ToolName>{selectedTool.name}</ToolName>
            <ToolDescription>{selectedTool.description}</ToolDescription>

            {renderParamForm()}

            {selectedTool.is_write && (
              <ConfirmRow>
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={e => setConfirmed(e.target.checked)}
                />
                <span>Confirm write operation (required for write tools)</span>
              </ConfirmRow>
            )}

            <RunButton
              onClick={handleRun}
              disabled={running || (selectedTool.is_write && !confirmed)}
              label={running ? 'Running...' : 'Run Test'}
            />

            {result && (
              <>
                <ResultMeta>
                  {result.execution_time_ms && `${result.execution_time_ms}ms`}
                  {result.error && ' | Error'}
                </ResultMeta>
                <ResultBox>{JSON.stringify(result, null, 2)}</ResultBox>
              </>
            )}
          </DetailPanel>
        ) : (
          <EmptyState>Select a tool from the list to test it</EmptyState>
        )}
      </RightPanel>
    </Container>
  );
}

export default ToolsTab;
